'use client'

import React, { useEffect, useRef } from 'react'
import {
  Chart,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController,
  PieController,
  DoughnutController,
  RadarController,
  type ChartConfiguration,
} from 'chart.js'

// Register Chart.js components and controllers
Chart.register(
  BarController,
  LineController,
  PieController,
  DoughnutController,
  RadarController,
  CategoryScale,
  LinearScale,
  RadialLinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
)

interface ChartRendererProps {
  config: ChartConfiguration
  className?: string
}

export function ChartRenderer({ config, className }: ChartRendererProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const chartRef = useRef<Chart | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Compute a sensible height based on data size to avoid tiny or huge charts
  const labels = Array.isArray((config as any)?.data?.labels)
    ? ((config as any).data.labels as any[])
    : []
  const itemCount = labels.length
  const isHorizontal = (config as any)?.options?.indexAxis === 'y'
  const perItem = isHorizontal ? 32 : 26
  const minHeight = 280
  const maxHeight = 800
  const computedHeight = Math.min(
    Math.max(itemCount * perItem, minHeight),
    maxHeight
  )

  useEffect(() => {
    if (!canvasRef.current) return

    // Destroy existing chart instance and clean up
    if (chartRef.current) {
      try {
        chartRef.current.destroy()
        chartRef.current = null
      } catch (error) {
        console.warn('Error destroying previous chart:', error)
      }
    }

    // Prepare a copy of config with readability tweaks (non-destructive)
    const cfg: ChartConfiguration = JSON.parse(JSON.stringify(config))

    const indexAxis = (cfg as any)?.options?.indexAxis || 'x'

    // Simple function to put each word on a new line - return array for Chart.js
    const putEachWordOnNewLine = (text: string) => {
      if (!text) return text
      // Split by any whitespace (handles multiple spaces) and filter empty strings
      const result = text.split(/\s+/).filter(word => word.length > 0)
      console.log('DEBUG: putEachWordOnNewLine', {
        input: text,
        output: result,
      })
      return result
    }

    // Get original labels before any modifications
    const originalLabels = Array.isArray((config as any)?.data?.labels)
      ? ((config as any).data.labels as any[])
      : []

    // Ensure options exist
    cfg.options = cfg.options || {}
    ;(cfg.options as any).responsive = true
    ;(cfg.options as any).maintainAspectRatio = false // Allow chart to fill container height

    // Get datasets for data analysis
    const datasets = Array.isArray((cfg as any)?.data?.datasets)
      ? ((cfg as any).data.datasets as any[])
      : []

    // Axis ticks configuration - NO TRUNCATION, show full labels
    const scales = ((cfg.options as any).scales =
      (cfg.options as any).scales || {})
    const x = (scales.x = scales.x || {})
    const y = (scales.y = scales.y || {})
    x.ticks = x.ticks || {}
    y.ticks = y.ticks || {}

    // SMART FIX: Handle both small positive values and negative values correctly
    // Get all data values to determine if we have negative numbers
    const allDataValues = datasets.flatMap(ds => ds?.data || [])
    const numericValues = allDataValues.filter(
      v => typeof v === 'number' && !isNaN(v)
    )
    const hasNegativeValues = numericValues.some(v => v < 0)

    if (indexAxis === 'x') {
      // x-axis is category, y-axis is numeric
      if (hasNegativeValues) {
        // Has negative data - let Chart.js show full range including negatives
        y.beginAtZero = false
      } else {
        // All positive data - start at 0 to show small values
        y.beginAtZero = true
      }
    } else {
      // y-axis is category, x-axis is numeric (horizontal bar)
      if (hasNegativeValues) {
        // Has negative data - let Chart.js show full range including negatives
        x.beginAtZero = false
      } else {
        // All positive data - start at 0 to show small values
        x.beginAtZero = true
      }
    }

    // Show full labels with each word on new line - map category axis ticks to multi-line labels
    console.log('DEBUG: Chart label setup', { indexAxis, originalLabels })
    if (!x.ticks.callback) {
      if (indexAxis === 'x') {
        // category x-axis - put each word on new line
        x.ticks.callback = (_value: any, index: number) => {
          const label = originalLabels[index] ?? index
          console.log('DEBUG: x-axis callback', {
            index,
            label,
            type: typeof label,
          })
          return putEachWordOnNewLine(String(label))
        }
      } else {
        // numeric x-axis (horizontal bar)
        x.ticks.callback = (value: any) => String(value)
      }
    }
    if (!y.ticks.callback) {
      if (indexAxis === 'y') {
        // category y-axis - put each word on new line
        y.ticks.callback = (_value: any, index: number) => {
          const label = originalLabels[index] ?? index
          console.log('DEBUG: y-axis callback', {
            index,
            label,
            type: typeof label,
          })
          return putEachWordOnNewLine(String(label))
        }
      } else {
        // numeric y-axis (vertical bar/line)
        y.ticks.callback = (value: any) => String(value)
      }
    }

    // Keep text horizontal and prevent auto-skipping to show all labels
    x.ticks.maxRotation = 0
    x.ticks.autoSkip = false // Show all labels, don't skip any
    x.ticks.autoSkipPadding = 20 // Add padding for multi-line labels
    y.ticks.maxRotation = 0
    y.ticks.autoSkip = false // Show all labels, don't skip any
    y.ticks.autoSkipPadding = 20 // Add padding for multi-line labels

    // Legend: hide if single dataset to save space
    const hasSingleDataset = datasets.length <= 1
    ;(cfg.options as any).plugins = (cfg.options as any).plugins || {}
    const legend = (((cfg.options as any).plugins as any).legend =
      ((cfg.options as any).plugins as any).legend || {})
    // For most charts, hide legend when single dataset to save space.
    // But for pie/doughnut, legend is how labels are shown — always display it.
    const chartType = (cfg as any)?.type || (config as any)?.type
    if (chartType === 'pie' || chartType === 'doughnut') {
      legend.display = true
      // Position legend below the chart to reduce clipping
      if (legend.position === undefined) legend.position = 'bottom'
      // Slightly smaller font to fit longer labels without affecting data
      const legendLabels = ((
        (cfg.options as any).plugins as any
      ).legend.labels =
        ((cfg.options as any).plugins as any).legend.labels || {})
      if (!legendLabels.font) legendLabels.font = { size: 11 }
    } else if (hasSingleDataset && legend.display === undefined) {
      legend.display = false
    }

    // Tooltip: show full, un-truncated label for clarity
    // originalLabels already defined above
    const tooltip = (((cfg.options as any).plugins as any).tooltip =
      ((cfg.options as any).plugins as any).tooltip || {})
    tooltip.callbacks = tooltip.callbacks || {}
    // For category charts, label comes from labels[index]
    if (!tooltip.callbacks.title) {
      tooltip.callbacks.title = (items: any[]) => {
        const idx = items?.[0]?.dataIndex ?? 0
        const val = originalLabels[idx]
        return String(val ?? '')
      }
    }
    if (!tooltip.callbacks.label) {
      tooltip.callbacks.label = (ctx: any) => {
        const dsLabel = ctx?.dataset?.label
        const indexAxis = (cfg as any)?.options?.indexAxis || 'x'

        // For horizontal charts, use x value; for vertical charts, use y value
        const value =
          indexAxis === 'y'
            ? (ctx?.parsed?.x ?? ctx?.raw) // Horizontal: prioritize x (numeric value)
            : (ctx?.parsed?.y ?? ctx?.raw) // Vertical: prioritize y (numeric value)

        if (dsLabel) return `${dsLabel}: ${value}`
        return String(value ?? '')
      }
    }

    // Create new chart with error handling
    try {
      chartRef.current = new Chart(canvasRef.current, cfg)
    } catch (error) {
      console.error('Failed to create chart:', error)
      // If chart creation fails, log the error but don't crash
    }

    // Ensure chart resizes when container changes (e.g., chat maximized)
    let resizeObserver: ResizeObserver | null = null
    if (containerRef.current && chartRef.current) {
      try {
        resizeObserver = new ResizeObserver(() => {
          try {
            // Force chart to recalculate dimensions
            if (chartRef.current) {
              chartRef.current.resize()
              // Also trigger a redraw to ensure everything updates
              chartRef.current.update('none')
            }
          } catch (e) {
            console.warn('Chart resize failed:', e)
          }
        })
        resizeObserver.observe(containerRef.current)
      } catch (e) {
        // ResizeObserver may not exist in some environments
        resizeObserver = null
      }
    }

    // Cleanup on unmount
    return () => {
      const container = containerRef.current
      if (resizeObserver && container) {
        try {
          resizeObserver.disconnect()
        } catch (e) {
          // ignore
        }
        resizeObserver = null
      }
      if (chartRef.current) {
        try {
          chartRef.current.destroy()
        } catch (error) {
          console.warn('Error during chart cleanup:', error)
        }
        chartRef.current = null
      }
    }
  }, [config])

  return (
    <div
      ref={containerRef}
      className={className || 'my-4 p-4 bg-white rounded-lg border'}
      style={{
        width: '100%',
        minWidth: 'min(950px, 100%)',
        maxWidth: '100%',
        maxHeight: `${maxHeight}px`,
        overflow: 'auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          height: `${computedHeight}px`,
          width: '100%',
        }}
      />
    </div>
  )
}
